/* global Office */

import { findQuoteBoundaryChild } from "./quoteBoundary";

/**
 * Replace the compose body with the given content. Returns true on success.
 * No-op (returns false) when the task pane is not currently on a compose item.
 */
export async function setComposeBody(
  content: string,
  coercionType: Office.CoercionType = Office.CoercionType.Text,
): Promise<boolean> {
  const item = Office.context.mailbox?.item as Office.MessageCompose | undefined;
  if (!item || typeof item.body?.setAsync !== "function") return false;

  return new Promise((resolve) => {
    item.body.setAsync(content, { coercionType }, (result) => {
      resolve(result.status === Office.AsyncResultStatus.Succeeded);
    });
  });
}

/**
 * Replace the compose subject. No-op if not in compose mode.
 */
export async function setComposeSubject(subject: string): Promise<boolean> {
  const item = Office.context.mailbox?.item as Office.MessageCompose | undefined;
  if (!item || typeof item.subject?.setAsync !== "function") return false;

  return new Promise((resolve) => {
    item.subject.setAsync(subject, (result) => {
      resolve(result.status === Office.AsyncResultStatus.Succeeded);
    });
  });
}

/** True when the task pane is attached to a compose item. */
export function isComposeMode(): boolean {
  const item = Office.context.mailbox?.item as Office.MessageCompose | undefined;
  return !!item && typeof item.body?.setAsync === "function";
}

// ── Rewrite insertion (preserves quoted reply/forward history) ───────────────
//
// A plain `body.setAsync` replaces the ENTIRE body, which on a reply also wipes
// the quoted conversation history below the user's message (and flattens any
// formatting). For a rewrite we only want to swap the user's authored message
// and keep the quote intact. Outlook nests the quote markers (e.g. an <hr> and
// <div id="divRplyFwdMsg"> inside a wrapper <div>), so raw-string slicing cuts
// mid-structure and drops the <hr>. Instead we parse the body into a DOM, find
// the top-level <body> child that holds the quote, drop everything before it,
// and insert the rewrite above it — keeping the subtree well-formed.

/** Drop the user's authored nodes (everything before the quote boundary) and
 * insert the rewrite in their place. Returns the re-serialized body HTML, which
 * the DOM guarantees is balanced/well-formed. */
function spliceRewriteBeforeQuote(doc: Document, boundary: Element, rewriteHtml: string): string {
  const body = doc.body;
  while (body.firstChild && body.firstChild !== boundary) {
    body.removeChild(body.firstChild);
  }
  const tpl = doc.createElement("template");
  tpl.innerHTML = rewriteHtml;
  body.insertBefore(tpl.content, boundary);
  return body.innerHTML;
}

/** Convert the plain-text rewrite (blank-line-separated paragraphs, `\n` line
 * breaks) into HTML so formatting survives the insert. Uses <div> per paragraph
 * (matches OWA's own markup and avoids classic Word's large <p> margins) with a
 * <div><br></div> spacer between paragraphs. */
function rewriteToHtml(text: string): string {
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return text
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((para) => `<div>${esc(para).split("\n").join("<br>") || "<br>"}</div>`)
    .join("<div><br></div>");
}

function getBodyHtml(item: Office.MessageCompose): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      item.body.getAsync(Office.CoercionType.Html, (r) =>
        resolve(r.status === Office.AsyncResultStatus.Succeeded ? String(r.value) : null),
      );
    } catch {
      resolve(null);
    }
  });
}

/** Write `html` into the compose body as HTML. Returns true on success. */
function setBodyHtml(item: Office.MessageCompose, html: string): Promise<boolean> {
  return new Promise((resolve) => {
    item.body.setAsync(html, { coercionType: Office.CoercionType.Html }, (result) => {
      resolve(result.status === Office.AsyncResultStatus.Succeeded);
    });
  });
}

/**
 * Insert the rewrite into the active compose draft. On a reply/forward it
 * replaces only the user's authored message and PRESERVES the quoted thread
 * (incl. the <hr>) below it; on a brand-new email it writes the rewrite as the
 * whole body. Always writes HTML so paragraph formatting is preserved. Returns
 * true on success; false when not on a compose item.
 */
export async function applyRewriteToBody(rewriteText: string): Promise<boolean> {
  const item = Office.context.mailbox?.item as Office.MessageCompose | undefined;
  if (!item || typeof item.body?.setAsync !== "function") return false;

  const currentHtml = await getBodyHtml(item);
  const rewriteHtml = rewriteToHtml(rewriteText);

  if (currentHtml) {
    try {
      const doc = new DOMParser().parseFromString(currentHtml, "text/html");
      const boundary = findQuoteBoundaryChild(doc);
      if (boundary) {
        return setBodyHtml(item, spliceRewriteBeforeQuote(doc, boundary, rewriteHtml));
      }
    } catch {
      /* fall through to full replace */
    }
  }

  // Brand-new email, parse failure, or no detectable quote → write the formatted
  // rewrite as the whole body (HTML so formatting is preserved).
  return setBodyHtml(item, rewriteHtml);
}
